import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Heart, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { storeApi, formatPrice, productUrl } from "@/lib/storefront";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { toast } from "sonner";

/**
 * Lightweight Quick-View modal — fetches full product detail by id/slug then
 * lets the user add to bag without leaving the catalog page.
 */
export default function QuickView({ open, productId, onClose }) {
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (!open || !productId) return;
    setLoading(true);
    setData(null);
    setQty(1);
    setActiveImg(0);
    storeApi
      .get(`/products/${productId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, productId]);

  // Lock body scroll & ESC handler
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const product = data;
  const hasSale = product?.old_price && product.old_price > product.price;
  const soldOut = product && (product.stock || 0) <= 0 && !product.allow_backorder;
  const images = product?.images?.length ? product.images : (product?.primary_image ? [product.primary_image] : []);
  const isWished = product ? has(product.id) : false;

  const handleAdd = () => {
    if (!product || soldOut) {
      toast.error("Out of stock");
      return;
    }
    addItem(product, qty);
    toast.success(`${product.title} added to bag`);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 backdrop-blur-[2px] md:items-center md:p-6"
      onClick={onClose}
      data-testid="quickview-overlay"
      aria-modal="true"
      role="dialog"
    >
      <div
        className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white md:max-w-4xl md:rounded-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="quickview-modal"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-[#1A1A1A] shadow-sm hover:bg-[#E8E2D2]"
          aria-label="Close"
          data-testid="quickview-close"
        >
          <X size={18} />
        </button>

        {loading || !product ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-[#DDD6C5] border-t-[#7D8471]" />
          </div>
        ) : (
          <div className="grid gap-0 md:grid-cols-2">
            {/* Image */}
            <div className="bg-[#E8E2D2] md:aspect-square">
              <div className="relative aspect-[4/5] md:aspect-square">
                {images[activeImg] ? (
                  <img
                    src={images[activeImg]}
                    alt={product.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center font-display text-2xl text-[#2D2823]/40">
                    No image
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 p-3">
                  {images.slice(0, 5).map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`h-12 w-12 flex-shrink-0 overflow-hidden rounded border-2 ${activeImg === i ? "border-[#1A1A1A]" : "border-transparent"}`}
                      aria-label={`View image ${i + 1}`}
                    >
                      <img src={src} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col gap-4 p-6 md:p-8">
              {product.brand?.name && (
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#2D2823]" data-testid="quickview-brand">{product.brand.name}</div>
              )}
              <h2 className="font-display text-2xl leading-tight text-[#1A1A1A] md:text-3xl" data-testid="quickview-title">{product.title}</h2>

              <div className="flex items-baseline gap-3 store-nums">
                <span className="text-xl font-medium text-[#1A1A1A]">{formatPrice(product.price, product.currency)}</span>
                {hasSale && (
                  <>
                    <span className="text-sm text-[#2D2823] line-through">{formatPrice(product.old_price, product.currency)}</span>
                    <span className="rounded-full bg-[#1A1A1A] px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-white">
                      Save {Math.round((1 - product.price / product.old_price) * 100)}%
                    </span>
                  </>
                )}
              </div>

              {product.short_description && (
                <p className="text-sm leading-relaxed text-[#2D2823]">{product.short_description}</p>
              )}

              <div className={`text-xs uppercase tracking-[0.2em] ${soldOut ? "text-rose-600" : "text-[#7D8471]"}`}>
                <span
                  className="mr-2 inline-block h-1.5 w-1.5 rounded-full align-middle"
                  style={{ background: soldOut ? "currentColor" : "#7D8471" }}
                />
                {soldOut ? "Out of stock" : "In stock"}
              </div>

              {/* Qty + Add */}
              <div className="mt-2 flex items-center gap-3">
                <div className="flex items-center gap-1 rounded-full border border-[#DDD6C5] bg-white">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2.5" aria-label="Decrease"><Minus size={14} /></button>
                  <span className="min-w-[24px] text-center text-sm store-nums" data-testid="quickview-qty">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2.5" aria-label="Increase"><Plus size={14} /></button>
                </div>
                <button
                  onClick={handleAdd}
                  disabled={soldOut}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-5 py-3 text-sm text-white transition-colors hover:bg-[#7D8471] disabled:bg-[#2D2823]/40"
                  data-testid="quickview-add-btn"
                >
                  <ShoppingBag size={14} /> {soldOut ? "Sold out" : "Add to bag"}
                </button>
                <button
                  onClick={() => toggle(product)}
                  className="rounded-full border border-[#DDD6C5] bg-white p-3 hover:bg-[#E8E2D2]"
                  aria-label="Wishlist"
                  data-testid="quickview-wishlist"
                >
                  <Heart size={14} className={isWished ? "fill-[#7D8471] text-[#7D8471]" : ""} />
                </button>
              </div>

              <Link
                to={productUrl(product)}
                onClick={onClose}
                className="mt-1 inline-flex items-center gap-1 text-sm text-[#7D8471] hover:underline"
                data-testid="quickview-view-full"
              >
                View full details <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
